import { ActiveChannelTypeEnum, TENANT_TYPES } from 'constants-channel';
import { getDisplayName } from 'lane-shared/helpers';
import { Channel } from 'application-types';
import { PropertyTenantChannel } from 'react-hooks';

import type { OrganisationGroup, PropertyGroup } from './types';

export function isPropertyChannel(channel: PropertyTenantChannel): boolean {
  return channel.type === ActiveChannelTypeEnum.Property;
}

export function isTenantChannel(channel: Channel): boolean {
  return TENANT_TYPES.includes(channel.type);
}

export function buildOrganisationGroups(
  channels: Channel[],
  tenantsByPropertyId?: Map<string, PropertyTenantChannel[]>
): OrganisationGroup[] {
  const properties = channels.filter(isPropertyChannel);
  const userTenants = channels.filter(isTenantChannel);

  function withTenants(property: Channel): PropertyGroup {
    const tenants = tenantsByPropertyId
      ? tenantsByPropertyId.get(property._id) ?? []
      : userTenants.filter(tenant => tenant.parent?._id === property._id);

    return { property, tenants };
  }

  const organisationIds = Array.from(
    new Set(
      properties
        .map(property => property.parent?._id)
        .filter((id): id is string => Boolean(id))
    )
  );

  const groupedByOrg: OrganisationGroup[] = organisationIds.map(
    organisationId => ({
      organisation:
        channels.find(channel => channel._id === organisationId) ?? null,
      properties: properties
        .filter(property => property.parent?._id === organisationId)
        .map(withTenants),
    })
  );

  const ungroupedProperties = properties.filter(
    p => !p.parent?._id && !organisationIds.includes(p._id)
  );

  if (ungroupedProperties.length > 0) {
    groupedByOrg.push({
      organisation: null,
      properties: ungroupedProperties.map(withTenants),
    });
  }

  return groupedByOrg;
}

export function filterGroups(
  groups: OrganisationGroup[],
  query: string
): OrganisationGroup[] {
  const trimmed = query.trim().toLowerCase();

  if (!trimmed) return groups;

  return groups
    .map(group => {
      const orgMatches =
        group.organisation &&
        getDisplayName(group.organisation).toLowerCase().includes(trimmed);

      const filteredProperties = group.properties
        .map(({ property, tenants }) => {
          const propertyMatches = getDisplayName(property)
            .toLowerCase()
            .includes(trimmed);
          const filteredTenants = tenants.filter(tenant =>
            getDisplayName(tenant).toLowerCase().includes(trimmed)
          );

          if (propertyMatches || filteredTenants.length > 0) {
            return {
              property,
              tenants: propertyMatches ? tenants : filteredTenants,
            };
          }

          return null;
        })
        .filter(
          (propertyGroup): propertyGroup is PropertyGroup =>
            propertyGroup !== null
        );

      if (orgMatches || filteredProperties.length > 0) {
        return { ...group, properties: filteredProperties };
      }

      return null;
    })
    .filter(
      (organisationGroup): organisationGroup is OrganisationGroup =>
        organisationGroup !== null
    );
}

export function findInitialExpandedOrgId(
  groups: OrganisationGroup[],
  currentHomeChannelId?: string | null
): string | null {
  if (!currentHomeChannelId) return null;

  for (const group of groups) {
    for (const { property, tenants } of group.properties) {
      if (
        property._id === currentHomeChannelId ||
        tenants.some(tenant => tenant._id === currentHomeChannelId)
      ) {
        return group.organisation?._id ?? null;
      }
    }
  }

  return null;
}

export function findInitialExpandedPropertyId(
  groups: OrganisationGroup[],
  currentHomeChannelId?: string | null
): string | null {
  if (!currentHomeChannelId) return null;

  for (const { properties } of groups) {
    for (const { property, tenants } of properties) {
      if (tenants.some(tenant => tenant._id === currentHomeChannelId)) {
        return property._id;
      }
    }
  }

  return null;
}
